import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Tag, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";

/**
 * CollectionSetup — the "Create your first category" screen that fires
 * immediately after a new collection is created. Replaces the prior
 * dead-end of dropping the user into an empty CollectionDetail page.
 *
 * Flow:
 *   Collections.jsx (create) → /collections/:id/setup → POST /api/categories
 *     → navigate /collections/:id
 *
 * If the user reaches this URL but the collection already has at least one
 * category (e.g. browser back/forward), we just hop them onwards — the
 * screen is strictly an onboarding step, not a permanent destination.
 */
export default function CollectionSetup() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [collection, setCollection] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Resolve the collection + bounce if categories already exist.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [colRes, catRes] = await Promise.all([
          apiClient.get(`/collections/${id}`),
          apiClient.get(`/categories`),
        ]);
        if (!mounted) return;
        setCollection(colRes.data);
        const myCats = (catRes.data || []).filter((c) => c.collection_id === id);
        if (myCats.length > 0) {
          navigate(`/collections/${id}`, { replace: true });
          return;
        }
      } catch {
        if (mounted) {
          toast.error("Couldn't load this collection.");
          navigate("/collections", { replace: true });
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [id, navigate]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const trimmed = categoryName.trim();
    if (!trimmed) {
      toast.error("Category name is required.");
      return;
    }
    setSaving(true);
    try {
      await apiClient.post("/categories", {
        collection_id: id,
        category_name: trimmed,
      });
      toast.success(`Category "${trimmed}" created.`);
      navigate(`/collections/${id}`);
    } catch (err) {
      const raw = err?.response?.data?.detail;
      const msg = Array.isArray(raw)
        ? raw.map((er) => `${(er.loc || []).slice(-1)[0] || "field"}: ${er.msg}`).join("; ")
        : (typeof raw === "string" ? raw : "Failed to create category.");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-2xl px-5 sm:px-6 py-12" data-testid="setup-loading">
        <Loader2 size={20} className="animate-spin vp-muted" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-5 sm:px-6 py-10 sm:py-14" data-testid="collection-setup">
      <nav aria-label="Breadcrumb" className="mb-4 text-xs flex items-center gap-2 flex-wrap" data-testid="setup-breadcrumb">
        <Link to="/" className="px-2 py-0.5 rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] uppercase tracking-wider hover:opacity-80">Home</Link>
        <span className="vp-muted" aria-hidden="true">→</span>
        <Link to="/collections" className="px-2 py-0.5 rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] uppercase tracking-wider hover:opacity-80">Collections</Link>
        <span className="vp-muted" aria-hidden="true">→</span>
        <span className="font-medium" data-testid="setup-breadcrumb-current">{collection?.name || "Collection"}</span>
      </nav>

      <p className="vp-label mb-2">Step 1 of 1 · Onboarding</p>
      <h1 className="text-3xl sm:text-4xl font-semibold leading-tight mb-2" data-testid="setup-title">
        Create your first category
      </h1>
      <p className="text-base vp-muted mb-8" data-testid="setup-subtitle">
        Every collection needs at least one category before you can add items.
      </p>

      <form onSubmit={submit} className="vp-card p-6 sm:p-8" data-testid="setup-form">
        <label htmlFor="setup-category-name" className="block text-xs font-medium mb-1.5 vp-muted">
          Category Name <span className="text-[hsl(var(--loss))]">*</span>
        </label>
        <input
          id="setup-category-name"
          data-testid="setup-category-name"
          type="text"
          autoFocus
          value={categoryName}
          onChange={(e) => setCategoryName(e.target.value)}
          placeholder={collection?.name?.toLowerCase().includes("wine") ? "e.g. France, New Zealand, Australia…" : "e.g. Books, Coins, Vinyl…"}
          className="vp-input"
          disabled={saving}
        />
        <p className="vp-muted text-xs mt-2">
          Tip: most collectors group by region, era, or material. You can rename or add more categories later.
        </p>

        <div className="flex items-center justify-end gap-2 mt-6">
          <Link
            to="/collections"
            className="vp-btn vp-btn-secondary"
            data-testid="setup-skip-back"
          >
            Back to Collections
          </Link>
          <button
            type="submit"
            disabled={saving || !categoryName.trim()}
            data-testid="setup-create-category"
            className="vp-btn vp-btn-primary"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Tag size={16} />}
            Create Category
            {!saving && <ArrowRight size={14} className="opacity-70" />}
          </button>
        </div>
      </form>
    </div>
  );
}
